import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import api, { getApiBaseUrl } from '../utils/axios';
import PageMeta from './PageMeta';
import './Login.css';

const Login = () => {
  const { login, backendReachable } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  // Registratie state
  const [showRegister, setShowRegister] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/auth/status`, { withCredentials: true });
      setAuthStatus(response.data);
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login`, {
        email,
        password
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        login(response.data);
      }
    } catch (err) {
      const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network'));
      const errorMessage = isNetworkError
        ? 'Backend server draait niet. Start deze in de projectmap met: npm start'
        : (err.response?.data?.error || err.message || 'Login failed');
      console.error('Login error:', err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // If under construction mode, show construction page
  if (authStatus?.isUnderConstruction) {
    return (
      <div className="login-container">
        <div className="construction-message">
          <h1>🚧 Under Construction</h1>
          <p>Deze applicatie is momenteel in ontwikkeling.</p>
          <button 
            className="btn btn-primary"
            onClick={() => login({ isUnderConstruction: true, role: 'guest' })}
          >
            Doorgaan naar applicatie
          </button>
        </div>
      </div>
    );
  }

  // If auth not required, skip login
  if (authStatus && !authStatus.requiresAuth) {
    login({ isUnderConstruction: true, role: 'guest' });
    return null;
  }

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError(null);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Opvragen mislukt. Probeer het later opnieuw.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (regPassword !== regPasswordConfirm) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }
    if (regPassword.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens zijn.');
      return;
    }
    setRegLoading(true);
    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/register`, {
        email: regEmail,
        password: regPassword,
        name: regName || undefined
      }, { withCredentials: true });
      if (response.data.success) {
        login(response.data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Registratie mislukt.';
      setError(msg);
    } finally {
      setRegLoading(false);
    }
  };

  if (showRegister && !authStatus?.registrationDisabled) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/logo-offerterunner.png" alt="OfferteRunner" className="login-logo" />
            <p className="tagline">Maak een gratis account aan</p>
          </div>
          <form onSubmit={handleRegisterSubmit}>
            {error && <div className="error">{error}</div>}
            <div className="input-group">
              <label htmlFor="reg-name">Naam</label>
              <input
                id="reg-name"
                type="text"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="Uw volledige naam"
                autoFocus
              />
            </div>
            <div className="input-group">
              <label htmlFor="reg-email">E-mailadres</label>
              <input
                id="reg-email"
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="uw@email.nl"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="reg-password">Wachtwoord</label>
              <input
                id="reg-password"
                type="password"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder="Minimaal 6 tekens"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="reg-password-confirm">Wachtwoord bevestigen</label>
              <input
                id="reg-password-confirm"
                type="password"
                value={regPasswordConfirm}
                onChange={(e) => setRegPasswordConfirm(e.target.value)}
                placeholder="Herhaal wachtwoord"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={regLoading}>
              {regLoading ? 'Registreren...' : 'Registreren'}
            </button>
            <p className="register-trial-note">
              Na registratie kunt u OfferteRunner 14 dagen gratis uitproberen.
            </p>
            <div className="login-forgot">
              <button
                type="button"
                className="btn-link"
                onClick={() => { setShowRegister(false); setError(null); }}
              >
                Al een account? Inloggen
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (forgotPassword) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/logo-offerterunner.png" alt="OfferteRunner" className="login-logo" />
            <p className="tagline">Wachtwoord vergeten</p>
          </div>
          {forgotSent ? (
            <>
              <div className="success" style={{ marginBottom: 16 }}>
                Als dit e-mailadres bij ons bekend is, ontvangt u een e-mail met een nieuw wachtwoord. Controleer ook uw spam-map.
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => { setForgotPassword(false); setForgotSent(false); }}>
                Terug naar inloggen
              </button>
            </>
          ) : (
            <form onSubmit={handleForgotSubmit}>
              {error && <div className="error">{error}</div>}
              <div className="input-group">
                <label htmlFor="forgot-email">E-mailadres</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="uw@email.nl"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                {forgotLoading ? 'Versturen...' : 'Stuur nieuw wachtwoord'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: 12, marginLeft: 0 }}
                onClick={() => { setForgotPassword(false); setError(null); }}
              >
                Terug naar inloggen
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <PageMeta title="Inloggen" description="Log in op OfferteRunner om professionele offertes te maken. Koppeling met Odoo en AI-teksten." />
      <div className="login-card">
        <div className="login-header">
          <img src="/logo-offerterunner.png" alt="OfferteRunner" className="login-logo" />
          <p className="tagline">Professionele offertes in minuten</p>
        </div>

        {backendReachable === false && (
          <div className="error" style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
            <strong>Backend server niet bereikbaar.</strong>
            <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
              Ga in een terminal naar de projectmap en start de server:
            </p>
            <code style={{ display: 'block', marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '13px' }}>
              npm start
            </code>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6b7280' }}>
              Laat die terminal open staan en ververs daarna deze pagina.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && <div className="error">{error}</div>}

          <div className="input-group">
            <label htmlFor="email">E-mailadres</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="uw@email.nl"
              required
              autoFocus
            />
          </div>

          <div className="input-group">
            <label htmlFor="password">Wachtwoord</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Wachtwoord"
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Inloggen...' : 'Inloggen'}
          </button>

          <div className="login-forgot">
            <button
              type="button"
              className="btn-link"
              onClick={() => { setForgotPassword(true); setError(null); }}
            >
              Wachtwoord vergeten?
            </button>
          </div>
          <div className="login-register-link">
            {authStatus?.registrationDisabled ? (
              <p style={{ fontSize: 13, color: '#991b1b', margin: 0, textAlign: 'center' }}>
                Registraties zijn momenteel niet mogelijk.
              </p>
            ) : (
              <button
                type="button"
                className="btn-link"
                onClick={() => { setShowRegister(true); setError(null); }}
              >
                Nog geen account? Registreer gratis
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
